import { gql } from '@apollo/client';
import getErrorMessage from './get-error-message';

const PB_TEMPLATE_ENTITY = 'page_builder_template';
const PB_SAVED_COMPONENT_ENTITY = 'page_builder_saved_component';

const SOURCE_QUERIES = {
  [PB_TEMPLATE_ENTITY]: gql`
    query previewTemplate($id: String!) {
      source: getPageBuilderTemplate(id: $id) {
        id
        name
        previewRecordEntity
        previewRecordId
      }
    }
  `,
  [PB_SAVED_COMPONENT_ENTITY]: gql`
    query previewSavedComponent($id: String!) {
      source: getPageBuilderSavedComponent(id: $id) {
        id
        name
        previewRecordEntity
        previewRecordId
      }
    }
  `
};

// Inline config literals: resolveDrafts is only honoured by the API for
// requesters with draft read access (admin users, preview tokens).
const MERGE_DRAFTS_QUERY = gql`
  query previewMerge($parent: MergePageBuilderTemplateChangesParent!, $entityIdentifier: String!) {
    components: mergePageBuilderTemplateChanges(
      parent: $parent
      entityIdentifier: $entityIdentifier
      config: { returnFullTree: false, resolveDrafts: true }
    )
  }
`;

const isPageBuilderPreviewEntity = itemEntity =>
  itemEntity === PB_TEMPLATE_ENTITY || itemEntity === PB_SAVED_COMPONENT_ENTITY;

// Build the merge query parent from ids only — the merge query fetches the
// record server-side when pageBuilder is absent and honours an explicitly
// passed pageBuilderTemplateId (how template preview forces the previewed
// template onto the record). Sending the full record tree here is not an
// option: the frontend client sends persisted queries as GETs, and large
// variables overflow proxy URL limits (414).
const getPreviewParent = ({ source, itemEntity }) => {
  const { previewRecordEntity, previewRecordId } = source;

  if (!previewRecordEntity || !previewRecordId) {
    // Standalone: the merge query fetches the template/saved component row
    // itself and renders its own tree (it has no template of its own).
    return {
      parent: { id: source.id, pageBuilderTemplateId: '' },
      entityIdentifier: itemEntity
    };
  }

  // For saved components the record keeps its own template ('' falls back to
  // the record's stored template id server-side).
  const forcedTemplateId = itemEntity === PB_TEMPLATE_ENTITY ? source.id : '';

  return {
    parent: { id: previewRecordId, pageBuilderTemplateId: forcedTemplateId },
    entityIdentifier: previewRecordEntity
  };
};

const getPageBuilderPreviewData = async ({ apolloClient, itemId, itemEntity }) => {
  try {
    const {
      data: { source }
    } = await apolloClient.query({
      query: SOURCE_QUERIES[itemEntity],
      variables: { id: itemId }
    });

    const { parent, entityIdentifier } = getPreviewParent({ source, itemEntity });

    const {
      data: { components }
    } = await apolloClient.query({
      query: MERGE_DRAFTS_QUERY,
      variables: { parent, entityIdentifier }
    });

    return {
      pbPreviewData: {
        components: components || [],
        sourceName: source.name,
        itemId,
        itemEntity
      }
    };
  } catch (error) {
    return getErrorMessage(error);
  }
};

export default getPageBuilderPreviewData;
export { isPageBuilderPreviewEntity };
